
// E:\My Mob Projects\My Portfolioes Websites\shadi-arts-portfolio Firebase\client\src\components\Admin\AnalyticsDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';

const AnalyticsDashboard = () => {
  const { t } = useLanguage();
  const [analyticsData, setAnalyticsData] = useState({
    totalVisitors: 0,
    youtubeVisitors: 0,
    tiktokVisitors: 0,
    directVisitors: 0,
    totalOrders: 0,
    pageViews: {},
    popularArtworks: [],
    conversionRate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = () => {
    try {
      // جلب البيانات من localStorage
      const events = JSON.parse(localStorage.getItem('shadiArtsAnalytics') || '[]');
      
      // تحليل المصادر
      let youtube = 0, tiktok = 0, direct = 0;
      const uniqueSessions = new Set();
      const pageViewsCount = {};
      const artworkClicks = {};

      events.forEach(event => {
        if (event.sessionId) uniqueSessions.add(event.sessionId);
        
        // تتبع المصادر من referrer
        if (event.name === 'page_view') {
          const referrer = event.referrer || '';
          if (referrer.includes('youtube.com')) youtube++;
          else if (referrer.includes('tiktok.com')) tiktok++;
          else direct++;
          
          // تسجيل مشاهدات الصفحات
          const page = event.data?.page || 'unknown';
          pageViewsCount[page] = (pageViewsCount[page] || 0) + 1;
        }
        
        // تتبع artworks الأكثر مشاهدة
        if (event.name === 'artwork_click' && event.data?.artworkTitle) {
          const title = event.data.artworkTitle;
          artworkClicks[title] = (artworkClicks[title] || 0) + 1;
        }
      });

      // ترتيب artworks الأكثر مشاهدة
      const popularArtworks = Object.entries(artworkClicks)
        .map(([title, clicks]) => ({ title, clicks }))
        .sort((a, b) => b.clicks - a.clicks)
        .slice(0, 5);

      // حساب نسبة التحويل (Orders / Unique Visitors)
      const orders = events.filter(e => e.name === 'order_complete').length;
      const conversionRate = uniqueSessions.size > 0 
        ? (orders / uniqueSessions.size) * 100 
        : 0;

      setAnalyticsData({
        totalVisitors: uniqueSessions.size,
        youtubeVisitors: youtube,
        tiktokVisitors: tiktok,
        directVisitors: direct,
        totalOrders: orders,
        pageViews: pageViewsCount,
        popularArtworks,
        conversionRate: conversionRate.toFixed(2)
      });
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">{t("Loading analytics...", "جاري تحميل التحليلات...")}</div>;
  }

  return (
    <div className="analytics-dashboard">
      <h3>{t("📊 Traffic Analytics", "📊 تحليلات الزوار")}</h3>
      
      {/* إحصائيات سريعة */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <h4>{t("Total Visitors", "إجمالي الزوار")}</h4>
            <p className="stat-number">{analyticsData.totalVisitors}</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📦</div>
          <div className="stat-info">
            <h4>{t("Total Orders", "إجمالي الطلبات")}</h4>
            <p className="stat-number">{analyticsData.totalOrders}</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-info">
            <h4>{t("Conversion Rate", "نسبة التحويل")}</h4>
            <p className="stat-number">{analyticsData.conversionRate}%</p>
          </div>
        </div>
      </div>

      {/* مصادر الزوار */}
      <div className="stats-section">
        <h4>{t("Traffic Sources", "مصادر الزوار")}</h4>
        <div className="sources-grid">
          <div className="source-card youtube">
            <span className="source-icon">▶️</span>
            <span className="source-name">{t("YouTube", "يوتيوب")}</span>
            <span className="source-count">{analyticsData.youtubeVisitors}</span>
          </div>
          <div className="source-card tiktok">
            <span className="source-icon">🎵</span>
            <span className="source-name">{t("TikTok", "تيك توك")}</span>
            <span className="source-count">{analyticsData.tiktokVisitors}</span>
          </div>
          <div className="source-card direct">
            <span className="source-icon">🔗</span>
            <span className="source-name">{t("Direct", "مباشر")}</span>
            <span className="source-count">{analyticsData.directVisitors}</span>
          </div>
        </div>
      </div>

      {/* أشهر الصفحات */}
      <div className="stats-section">
        <h4>{t("Popular Pages", "أشهر الصفحات")}</h4>
        <div className="pages-list">
          {Object.entries(analyticsData.pageViews).slice(0, 5).map(([page, views]) => (
            <div key={page} className="page-item">
              <span className="page-name">{page}</span>
              <span className="page-views">{views} {t("views", "مشاهدة")}</span>
            </div>
          ))}
        </div>
      </div>

      {/* أشهر الأعمال الفنية */}
      {analyticsData.popularArtworks.length > 0 && (
        <div className="stats-section">
          <h4>{t("Most Viewed Artworks", "الأعمال الفنية الأكثر مشاهدة")}</h4>
          <div className="artworks-list">
            {analyticsData.popularArtworks.map((artwork, idx) => (
              <div key={idx} className="artwork-item">
                <span className="artwork-rank">{idx + 1}</span>
                <span className="artwork-title">{artwork.title}</span>
                <span className="artwork-clicks">{artwork.clicks} {t("views", "مشاهدة")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="analytics-note">
        <p>💡 {t("Data is stored locally. Clear browser data to reset analytics.", "يتم تخزين البيانات محلياً. مسح بيانات المتصفح يؤدي إلى إعادة تعيين التحليلات.")}</p>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;